import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { CardTitle, Loading, typography } from "@dsaui/components";
import { cn } from "packages/dsaui/src/utils";
import routes from "routes";
import { useAppContext } from "utils/hooks";
import AuthenticationErrorPage from "pages/AuthenticationErrorPage";

interface LoginPageProps {
  loginFn: () => void | Promise<void>;
}

const LoginPage = ({ loginFn }: LoginPageProps) => {
  const { authenticated } = useAppContext();
  const { t } = useTranslation();
  const location = useLocation();
  const [loginFailed, setLoginFailed] = useState(false);
  const [loginAttempt, setLoginAttempt] = useState(0);
  const from =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ??
    routes.internal.base;

  useEffect(() => {
    if (authenticated) {
      return;
    }

    Promise.resolve(loginFn()).catch((error: unknown) => {
      console.error(error);
      setLoginFailed(true);
    });
  }, [authenticated, loginAttempt, loginFn]);

  if (authenticated) {
    return <Navigate to={from} replace />;
  }

  if (loginFailed) {
    return (
      <AuthenticationErrorPage
        onRetry={() => {
          setLoginFailed(false);
          setLoginAttempt((attempt) => attempt + 1);
        }}
      />
    );
  }

  return (
    <div className="bg-gray-50 px-4 py-10 sm:px-6 sm:py-12">
      <div className="mx-auto flex w-full max-w-2xl flex-col items-center gap-4 text-center">
        <CardTitle className={cn(typography.formPageTitle.className, "text-balance")}>
          {t("pages.login")}
        </CardTitle>
        <p className={cn(typography.formSubtitle.className, "text-center")}>
          {t("login.redirecting")}
        </p>
        <Loading />
      </div>
    </div>
  );
};

export default LoginPage;
